import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Settings, LogOut, ChevronDown, X, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './AdminPreviewBar.css';

const NAV_LINKS = [
  { to: '/coming-soon', label: 'Coming Soon' },
  { to: '/home', label: 'Home' },
  { to: '/about-us', label: 'About' },
  { to: '/founders', label: 'Founders' },
  { to: '/our-story', label: 'Our Story' },
  { to: '/cadence-promise', label: 'The Promise' },
  { to: '/demo', label: 'How It Works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/membership', label: 'Membership' },
];

// Routes that the public sees as a horizontal swipe deck (CurtainNavigation)
const CURTAIN_ROUTES = [
  '/home',
  '/about-us',
  '/founders',
  '/cadence-promise',
  '/our-story',
  '/pricing',
  '/membership',
  '/articles',
];

const PREVIEW_KEY = 'cadence_admin_preview_as_visitor';

const AdminPreviewBar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [previewAsVisitor, setPreviewAsVisitor] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(PREVIEW_KEY) === 'true'
  );
  const menuRef = useRef(null);
  const hideTimerRef = useRef(null);

  const isAdmin = isAuthenticated && user && ['admin', 'staff', 'reviewer', 'provider'].includes(user.role);
  const isCurtainPage = CURTAIN_ROUTES.includes(location.pathname);

  const togglePreviewAsVisitor = () => {
    const next = !previewAsVisitor;
    setPreviewAsVisitor(next);
    try {
      if (next) localStorage.setItem(PREVIEW_KEY, 'true');
      else localStorage.removeItem(PREVIEW_KEY);
    } catch {/* ignore */}
    // Hard reload so the AppLayout swap takes effect cleanly
    setTimeout(() => window.location.reload(), 80);
  };

  // Auto-hide after 5 seconds on desktop (resets on hover)
  const startHideTimer = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
      setUserInteracted(true);
    }, 5000);
  };

  useEffect(() => {
    startHideTimer();
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const handleMouseEnterDesktop = () => {
    setIsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };

  const handleMouseLeaveDesktop = () => {
    startHideTimer();
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY < lastScrollY || currentScrollY < 50) {
        setIsVisible(true);
        startHideTimer();
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        setMobileMenuOpen(false);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  if (!isAdmin) return null;

  const handleLogout = () => {
    logout();
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* DESKTOP: Floating tiles (unchanged) */}
      <div
        className={`admin-preview-bar admin-desktop-only ${isVisible ? 'visible' : ''}`}
        onMouseEnter={handleMouseEnterDesktop}
        onMouseLeave={handleMouseLeaveDesktop}
      >
        <Link to="/admin" className="admin-preview-link" data-testid="back-to-admin-link">
          <Settings size={16} />
          <span>Admin</span>
        </Link>
        {NAV_LINKS.map((link) => (
          <Link key={link.to} to={link.to} className="admin-preview-nav-link">
            {link.label}
          </Link>
        ))}
        {isCurtainPage && (
          <button
            type="button"
            onClick={togglePreviewAsVisitor}
            className={`admin-preview-toggle ${previewAsVisitor ? 'active' : ''}`}
            data-testid="admin-preview-as-visitor-toggle"
            title={previewAsVisitor ? 'Switch back to admin scroll view' : 'Preview the public swipe deck'}
          >
            {previewAsVisitor ? <EyeOff size={14} /> : <Eye size={14} />}
            <span>{previewAsVisitor ? 'Admin View' : 'Visitor View'}</span>
          </button>
        )}
        <button onClick={handleLogout} className="admin-preview-logout" data-testid="admin-logout-btn">
          <LogOut size={14} />
          <span>Sign Out</span>
        </button>
      </div>

      {/* MOBILE: Compact floating button + dropdown */}
      <div
        className={`admin-mobile-wrapper ${isVisible ? 'visible' : ''}`}
        ref={menuRef}
        data-testid="admin-mobile-menu"
      >
        <button
          className="admin-mobile-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="admin-mobile-toggle"
        >
          <Settings size={14} />
          <span>Admin</span>
          {mobileMenuOpen ? <X size={12} /> : <ChevronDown size={12} />}
        </button>

        {mobileMenuOpen && (
          <div className="admin-mobile-dropdown" data-testid="admin-mobile-dropdown">
            <Link to="/admin" className="admin-mobile-item" onClick={handleNavClick}>
              <Settings size={13} />
              Dashboard
            </Link>
            <div className="admin-mobile-divider" />
            {NAV_LINKS.map((link) => (
              <Link key={link.to} to={link.to} className="admin-mobile-item" onClick={handleNavClick}>
                {link.label}
              </Link>
            ))}
            {isCurtainPage && (
              <>
                <div className="admin-mobile-divider" />
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    togglePreviewAsVisitor();
                  }}
                  className="admin-mobile-item admin-mobile-preview-toggle"
                  data-testid="admin-preview-as-visitor-toggle-mobile"
                >
                  {previewAsVisitor ? <EyeOff size={13} /> : <Eye size={13} />}
                  {previewAsVisitor ? 'Switch to Admin View' : 'Preview as Visitor (swipe)'}
                </button>
              </>
            )}
            <div className="admin-mobile-divider" />
            <button onClick={handleLogout} className="admin-mobile-item admin-mobile-signout">
              <LogOut size={13} />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminPreviewBar;
